import React, { useState } from "react";
import { useFetch } from "@/lib/api";
import { PageHeader, StatusChip, LoadingState, OutcomeChip, DemoBadge } from "@/components/app/primitives";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { LivePilotCandidatesPanel } from "@/components/app/liveModulePanels";

const SCORE_LABELS = {
  expected_impact: "Expected impact", speed_to_launch: "Speed to launch", data_readiness: "Data readiness",
  operational_feasibility: "Operational feasibility", cx_value: "CX value", owner_readiness: "Owner readiness",
  dependency_complexity: "Dependency complexity", measurement_clarity: "Measurement clarity", risk: "Risk",
};
const INVERTED = ["dependency_complexity", "risk"];

const ScoreBar = ({ label, value, inverted }) => (
  <div className="flex items-center gap-2">
    <span className="text-[10px] text-[--tx-3] w-36 shrink-0">{label}</span>
    <div className="flex-1 h-1.5 rounded-full bg-[--shell-3] overflow-hidden">
      <div className="h-full rounded-full" style={{ width: `${value * 20}%`, background: inverted ? (value >= 4 ? "var(--danger)" : value >= 3 ? "var(--amber)" : "var(--green)") : (value >= 4 ? "var(--green)" : value >= 3 ? "var(--ai)" : "var(--amber)") }} />
    </div>
    <span className="font-mono text-[10px] text-[--tx-2] w-4 text-right">{value}</span>
  </div>
);

const statusTone = (s) => s.includes("pilot-ready") ? "validated" : s.includes("Compliance") ? "risk" : s === "Design" ? "ai" : "neutral";

export default function PilotBoardPage() {
  const { data: pilots, loading } = useFetch("/pilots");
  const [selected, setSelected] = useState(null);

  if (loading) return <LoadingState message="Scoring pilot candidates..." />;

  const lanes = [
    { key: "Day 1-30", label: "First 30 days", accent: "text-[#E86A6A]", note: "Triad-gated, holdout-measured, launch Day 10" },
    { key: "Day 31-90", label: "Days 31-90", accent: "text-[--ai]", note: "Design in flight - gated on 30-day evidence" },
    { key: "Day 91-180", label: "Days 91-180", accent: "text-[--tx-2]", note: "Backlog - gated on data-quality milestones" },
  ];

  return (
    <div data-testid="pilots-page">
      <PageHeader
        kicker="Scene 8 - Pilot prioritization"
        title="Pilot Design & Prioritization Board"
        subtitle="Nine first-wave candidates scored across nine dimensions. Nothing is pilot-ready until it passes the Triad gate with a measurement plan locked."
        right={<DemoBadge />}
      />

      <LivePilotCandidatesPanel />

      <div className="grid lg:grid-cols-3 gap-4" data-testid="pilot-lanes">
        {lanes.map((lane) => (
          <div key={lane.key} data-testid={`pilot-lane-${lane.key.replace(/\s/g, "-").toLowerCase()}`}>
            <div className="mb-2.5 px-1">
              <h3 className={`font-display text-sm font-semibold ${lane.accent}`}>{lane.label}</h3>
              <p className="text-[10px] text-[--tx-3]">{lane.note}</p>
            </div>
            <div className="space-y-3">
              {(pilots || []).filter((p) => p.horizon === lane.key).map((p) => (
                <div key={p.id} onClick={() => setSelected(p)} data-testid={`pilot-card-${p.id}`}
                  className="rounded-xl border border-[--hairline] bg-[--panel-dark] p-3.5 cursor-pointer hover:border-[--heat-3] transition-colors">
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className="font-mono text-[10px] text-[--tx-3]">{p.id}</span>
                    <span className="font-mono text-[10px] text-[#E86A6A]">{p.rcc}</span>
                    <span className="ml-auto font-display text-sm font-bold text-[--tx-1] tabular-nums" title="Composite score">{p.composite}</span>
                  </div>
                  <h4 className="text-[13px] font-semibold text-[--tx-1] leading-snug mb-1.5">{p.name}</h4>
                  <StatusChip tone={statusTone(p.status)} className="mb-2">{p.status}</StatusChip>
                  <div className="space-y-1 mb-2">
                    <ScoreBar label="Expected impact" value={p.scores.expected_impact} />
                    <ScoreBar label="Speed to launch" value={p.scores.speed_to_launch} />
                    <ScoreBar label="Measurement clarity" value={p.scores.measurement_clarity} />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] text-[--tx-3]">Owner: <span className="text-[--tx-2]">{p.owner_role}</span></span>
                    <span className="text-[10px] text-[--ai]">Details →</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent className="bg-[--shell-2] border-[--hairline] w-full sm:max-w-md overflow-y-auto thin-scroll" data-testid="pilot-detail-drawer">
          {selected && (
            <>
              <SheetHeader>
                <SheetTitle className="text-[--tx-1] font-display flex items-center gap-2">
                  <span className="font-mono text-xs text-[--tx-3]">{selected.id}</span> {selected.name}
                </SheetTitle>
                <SheetDescription className="text-[--tx-3] text-xs">{selected.horizon} · {selected.rcc} · Composite {selected.composite}</SheetDescription>
              </SheetHeader>
              <div className="space-y-4 mt-4">
                <div className="flex flex-wrap gap-2">
                  <StatusChip tone={statusTone(selected.status)}>{selected.status}</StatusChip>
                  {selected.six_outcomes?.map((o) => <OutcomeChip key={o} outcome={o} />)}
                </div>
                <p className="text-[13px] text-[--tx-2] leading-relaxed">{selected.description}</p>
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-[--tx-3] mb-2">Full scoring (1-5)</div>
                  <div className="space-y-1.5">
                    {Object.entries(SCORE_LABELS).map(([k, l]) => (
                      <ScoreBar key={k} label={l + (INVERTED.includes(k) ? " (lower=better)" : "")} value={selected.scores[k]} inverted={INVERTED.includes(k)} />
                    ))}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-[--tx-3] mb-0.5">Triad gate</div>
                  <p className="text-xs text-[--tx-2]">{selected.triad_gate}</p>
                </div>
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-[--tx-3] mb-0.5">Measurement design</div>
                  <p className="text-xs text-[--tx-2] leading-relaxed">{selected.measurement_design}</p>
                </div>
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-[--tx-3] mb-1">Dependencies</div>
                  <div className="flex flex-wrap gap-1.5">{selected.dependencies?.map((d) => <StatusChip key={d} tone="risk">{d}</StatusChip>)}</div>
                </div>
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div><div className="text-[--tx-3] mb-0.5">Owner role</div><div className="text-[--tx-1] font-medium">{selected.owner_role}</div></div>
                  <div><div className="text-[--tx-3] mb-0.5">First signal</div><div className="text-[--tx-1] font-medium">{selected.first_signal}</div></div>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
